import type { ActionExecutor, TriggerEventContext } from "../TriggerEngine.interfaces";

export const switchScene: ActionExecutor = async (
  _radio,

  config,

  { emitAction, eventContext },
) => {
  const sceneId = config?.scene;

  if (!sceneId) throw new Error("A target scene is required");

  emitAction("switch_scene", { sceneId, eventContext });
};

// Transfer: moves resource from source to destination, clears source

export const toggleArea: ActionExecutor = async (
  radio,

  config,

  { emitAction, eventContext },
) => {
  const { fromScene, fromArea, toScene, toArea } = config;

  if (!fromScene || !fromArea || !toScene || !toArea) {
    throw new Error("Source and destination scenes and areas are required");
  }

  if (fromScene === toScene && fromArea === toArea) {
    throw new Error("Cannot transfer an area to itself");
  }

  const { openDatabase, saveSnapshotDatabase } = await import("../../Utils/db");

  const db = await openDatabase();

  // Keep source removal and destination assignment atomic, including transfers
  // between two areas in the same scene.
  const transferred = await db.transaction("rw", db.scenes, async () => {
    const scenes = await db.scenes.toArray();
    const srcScene = scenes.find((s) => s.id === fromScene);
    const dstScene =
      fromScene === toScene
        ? srcScene
        : scenes.find((s) => s.id === toScene);

    if (!srcScene || !dstScene) {
      throw new Error("The source or destination scene no longer exists");
    }

    const srcArea = srcScene.model.areas.find((a) => a.id === fromArea);
    const dstArea = dstScene.model.areas.find((a) => a.id === toArea);

    if (!srcArea || !dstArea) {
      throw new Error("The source or destination area no longer exists");
    }

    dstArea.media.resource = srcArea.media.resource;
    srcScene.model.areas = srcScene.model.areas.filter((a) => a.id !== fromArea);

    await db.scenes.update(srcScene.id, { model: srcScene.model });

    if (srcScene.id !== dstScene.id) {
      await db.scenes.update(dstScene.id, { model: dstScene.model });
    }

    return { activeScene: [srcScene, dstScene].find((scene) => scene.active) };
  });

  if (transferred.activeScene) {
    await radio.updateTemplate(transferred.activeScene.model, { eventContext });

    emitAction("studio_refreshed", { model: transferred.activeScene.model });
  }

  await saveSnapshotDatabase();
};

// Clone: copies resource from source to destination, keeps source intact

export const cloneArea: ActionExecutor = async (
  radio,

  config,

  { emitAction, eventContext },
) => {
  const { fromScene, fromArea, toScene, toArea } = config;

  if (!fromScene || !fromArea || !toScene || !toArea) {
    throw new Error("Source and destination scenes and areas are required");
  }

  const { openDatabase, saveSnapshotDatabase } = await import("../../Utils/db");

  const db = await openDatabase();

  const scenes = await db.scenes.toArray();

  const srcScene = scenes.find((s) => s.id === fromScene);

  const dstScene = scenes.find((s) => s.id === toScene);

  if (!srcScene || !dstScene) {
    throw new Error("The source or destination scene no longer exists");
  }

  const srcArea = srcScene.model.areas.find((a) => a.id === fromArea);

  const dstArea = dstScene.model.areas.find((a) => a.id === toArea);

  if (!srcArea || !dstArea) {
    throw new Error("The source or destination area no longer exists");
  }

  dstArea.media.resource = JSON.parse(JSON.stringify(srcArea.media.resource));

  await db.scenes.update(dstScene.id, { model: dstScene.model });

  const currentScene = scenes.find((s) => s.active);

  if (currentScene && currentScene.id === dstScene.id) {
    await radio.updateTemplate(currentScene.model, { eventContext });

    emitAction("studio_refreshed", { model: currentScene.model });
  }

  await saveSnapshotDatabase();
};

export const hideArea: ActionExecutor = async (
  radio,

  config,

  { emitAction, eventContext },
) => {
  const { scene: sceneId, areaId } = config;

  if (!sceneId || !areaId) throw new Error("A target scene and area are required");

  const { openDatabase, saveSnapshotDatabase } = await import("../../Utils/db");

  const db = await openDatabase();

  const scenes = await db.scenes.toArray();

  const scene = scenes.find((s) => s.id === sceneId);

  if (!scene) throw new Error("The target scene no longer exists");

  const area = scene.model.areas.find((a) => a.id === areaId);

  if (!area) throw new Error("The target area no longer exists");

  area.editorState = {
    ...area.editorState,

    isHidden: true,
  };

  await db.scenes.update(scene.id, { model: scene.model });

  const currentScene = scenes.find((s) => s.active);

  if (currentScene && currentScene.id === scene.id) {
    await radio.updateTemplate(currentScene.model, { eventContext });

    emitAction("studio_refreshed", { model: currentScene.model });
  }

  await saveSnapshotDatabase();
};

export const showArea: ActionExecutor = async (
  radio,

  config,

  { emitAction, eventContext },
) => {
  const { scene: sceneId, areaId } = config;

  if (!sceneId || !areaId) throw new Error("A target scene and area are required");

  const { openDatabase, saveSnapshotDatabase } = await import("../../Utils/db");

  const db = await openDatabase();

  const scenes = await db.scenes.toArray();

  const scene = scenes.find((s) => s.id === sceneId);

  if (!scene) throw new Error("The target scene no longer exists");

  const area = scene.model.areas.find((a) => a.id === areaId);

  if (!area) throw new Error("The target area no longer exists");

  area.editorState = {
    ...area.editorState,

    isHidden: false,
  };

  await db.scenes.update(scene.id, { model: scene.model });

  const currentScene = scenes.find((s) => s.active);

  if (currentScene && currentScene.id === scene.id) {
    await radio.updateTemplate(currentScene.model, { eventContext });

    emitAction("studio_refreshed", { model: currentScene.model });
  }

  await saveSnapshotDatabase();
};

const toggleSceneItem = async (
  radio: Parameters<ActionExecutor>[0],

  config: Record<string, string>,

  emitAction: (action: string, payload: Record<string, unknown>) => void,

  collection: "widgets" | "elements",

  active: boolean,

  eventContext?: TriggerEventContext,
) => {
  const { scene: sceneId, itemId } = config;

  if (!sceneId || !itemId) throw new Error("A target scene and item are required");

  const { openDatabase, saveSnapshotDatabase } = await import("../../Utils/db");

  const db = await openDatabase();

  const scenes = await db.scenes.toArray();

  const scene = scenes.find((s) => s.id === sceneId);

  if (!scene) throw new Error("The target scene no longer exists");

  const item = scene.model[collection]?.find((i) => i.id === itemId);

  if (!item) throw new Error("The target scene item no longer exists");

  item.active = active;

  await db.scenes.update(scene.id, { model: scene.model });

  const currentScene = scenes.find((s) => s.active);

  if (currentScene && currentScene.id === scene.id) {
    await radio.updateTemplate(currentScene.model, { eventContext });

    emitAction("studio_refreshed", { model: currentScene.model });
  }

  await saveSnapshotDatabase();
};

export const hideWidget: ActionExecutor = async (
  radio,

  config,

  { emitAction, eventContext },
) => toggleSceneItem(radio, config, emitAction, "widgets", false, eventContext);

export const showWidget: ActionExecutor = async (
  radio,

  config,

  { emitAction, eventContext },
) => toggleSceneItem(radio, config, emitAction, "widgets", true, eventContext);

export const hideElement: ActionExecutor = async (
  radio,

  config,

  { emitAction, eventContext },
) => toggleSceneItem(radio, config, emitAction, "elements", false, eventContext);

export const showElement: ActionExecutor = async (
  radio,

  config,

  { emitAction, eventContext },
) => toggleSceneItem(radio, config, emitAction, "elements", true, eventContext);
